/**
 * `c_TPA` profile-attribute readers + TPA branding restore (SFCC-11069 /
 * TPA-14) — CLIENT-SAFE. Sole parser of the blob, so its shape lives here once.
 *
 * A returning authenticated TPA user who cleared (or expired) their
 * `tpaBranding` cookie loses their TPA identity. SFRA falls back to the
 * customer profile (`profile.getTPABranding()` → `custom.TPA`); we mirror the
 * LIVE headless behaviour (hec-composable `setTpaBrandingCookiesFromCustomerData`
 * / `useSyncCookiesFromCustomerData`), which restores the cookie client-side
 * from the SCAPI-exposed `c_TPA` profile attribute on every authenticated render.
 *
 * This module is intentionally NOT `*.server.ts`: it's imported by a client hook,
 * so it can't depend on the server-only `tpa.server.ts`. The cookie name + AFID
 * param set are duplicated here from `tpa.server.ts` (keep them in sync) so the
 * client-set cookie round-trips with the server reader (`getAfidFromRequest`).
 */
import { z } from "zod";

import type { ShopperCustomersTypes } from "@tsf/commerce-sdk-isomorphic";

// Mirrors TPA_BRANDING_COOKIE / TPA_BRANDING_MAX_AGE in `tpa.server.ts`.
export const TPA_BRANDING_COOKIE = "tpaBranding";
const ONE_YEAR_MS = 31536000 * 1000;

// Mirrors getAfidFromUrl() param order in `tpa.server.ts`.
const AFID_URL_PARAMS = ["AFID", "afid", "a_aid"] as const;

export interface TpaBrandingRestore {
  /** Full cookie triple `{afid}|{groupName}|{cid}` (verbatim from the profile). */
  value: string;
  /** Cookie expiry, ms epoch. */
  expiresAt: number;
}

/**
 * Read the `c_TPA` profile attribute off a customer.
 *
 * The SDK `Customer` type exposes custom attributes only through its
 * `[key: string]: any` index signature, so `customer.c_TPA` is an `any` at
 * every call site. This pins it to `string | null` at runtime, once. Empty
 * string counts as absent (SCAPI omits the attribute for non-TPA shoppers).
 */
export function readCustomerCTPA(
  customer: ShopperCustomersTypes.Customer | null | undefined,
): string | null {
  const value: unknown = customer?.c_TPA;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * The `c_TPA` blob as SFRA writes it. Fields validate independently
 * (`.catch(undefined)`): a malformed `branding` must not take a valid
 * `mostRecentTPA` down with it, and vice versa.
 */
const cTpaBlobSchema = z.object({
  mostRecentTPA: z.string().min(1).optional().catch(undefined),
  branding: z
    .object({
      value: z.string().min(1),
      // Its own .catch: a non-number expires_in means "absent" (1-year
      // fallback), it must not invalidate the whole branding object.
      expires_in: z.number().optional().catch(undefined),
    })
    .optional()
    .catch(undefined),
});

function parseCTPA(
  cTPA: string | null | undefined,
): z.infer<typeof cTpaBlobSchema> | null {
  if (!cTPA) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(cTPA);
  } catch {
    return null; // malformed JSON on the profile attr
  }
  const parsed = cTpaBlobSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * The shopper's current TPA id, per SFRA's `Profile.getCurrentTPA()`
 * (`plugin_tpa/cartridge/models/profile.js:185-197`) → `mostRecentTPA`.
 *
 * Returns null for a branding-only blob (`{branding:{…}}`, written by an AFID
 * affiliate link) — that shopper is affiliate-tagged, not TPA-linked. SFRA has a
 * deprecated-attribute fallback that Workers cannot see, so callers must treat
 * null as "unknown", never as "definitely not a TPA shopper".
 */
export function getCurrentTpaId(
  cTPA: string | null | undefined,
): string | null {
  return parseCTPA(cTPA)?.mostRecentTPA ?? null;
}

/**
 * Shape class of a `c_TPA` value, for logs (COM-926). Never the value itself.
 *
 * - `absent`: null or empty (SCAPI omits the attribute for non-TPA shoppers).
 * - `has-tpa`: carries a usable `mostRecentTPA` (what `getCurrentTpaId` reads).
 * - `not-json`: does not parse as JSON.
 * - `not-object`: parses, but to an array, string, number or null.
 * - `empty-tpa`: has a `mostRecentTPA` key that is not a non-empty string.
 * - `branding-only`: no `mostRecentTPA` key, but a `branding` key (the AFID
 *   affiliate blob). Still unknown, not "not a TPA shopper": SFRA's deprecated
 *   fallback can make it one (see `getCurrentTpaId`).
 * - `no-fields`: an object with neither key.
 */
export type CTpaShape =
  | "absent"
  | "has-tpa"
  | "not-json"
  | "not-object"
  | "empty-tpa"
  | "branding-only"
  | "no-fields";

/** Classify a `c_TPA` value by shape. Same parse as `getCurrentTpaId`. */
export function classifyCTPA(cTPA: string | null | undefined): CTpaShape {
  if (!cTPA) return "absent";
  let raw: unknown;
  try {
    raw = JSON.parse(cTPA);
  } catch {
    return "not-json";
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return "not-object";
  }
  if (getCurrentTpaId(cTPA)) return "has-tpa";
  if ("mostRecentTPA" in raw) return "empty-tpa";
  if ("branding" in raw) return "branding-only";
  return "no-fields";
}

/**
 * Does the current URL already carry an AFID? If so, the server middleware owns
 * the cookie write (URL wins) and the client restore must stand down.
 */
export function urlHasAfid(search: string): boolean {
  const params = new URLSearchParams(search);
  return AFID_URL_PARAMS.some(p => Boolean(params.get(p)));
}

/**
 * Parse + validate the `c_TPA` profile attribute into a restorable cookie.
 * Returns null when there's nothing valid to restore. Guards mirror MRT:
 *   - `branding.value` must be a non-empty string,
 *   - it must contain a GroupName segment (`split("|")[1]`) — MRT's
 *     `if (!groupName) return`; a bare AFID with no group isn't restored,
 *   - `expires_in` (ms epoch) must not be in the past.
 */
export function getTpaBrandingRestoreCookie(
  cTPA: string | null | undefined,
): TpaBrandingRestore | null {
  const branding = parseCTPA(cTPA)?.branding;
  if (!branding) return null;

  // GroupName guard (MRT parity): the cookie triple is `{afid}|{groupName}|{cid}`;
  // restoring a value with no GroupName segment is treated as nothing to restore.
  if (!branding.value.split("|")[1]) return null;

  const expiresAt = branding.expires_in ?? Date.now() + ONE_YEAR_MS;
  if (expiresAt < Date.now()) return null; // expired — don't resurrect

  return { value: branding.value, expiresAt };
}

/**
 * Serialize a restore into a `document.cookie` string. Matches the server
 * writer in `tpa.server.ts`: `encodeURIComponent` the value but keep LITERAL
 * `|` delimiters (SFRA splits on `|`; an encoded `%7C` would read the whole
 * string as the AFID). Path `/`, SameSite=Lax, Secure on https, expiry from the
 * profile's `expires_in`.
 */
export function serializeTpaBrandingCookie(
  restore: TpaBrandingRestore,
  { secure }: { secure: boolean },
): string {
  const encoded = encodeURIComponent(restore.value).replace(/%7C/g, "|");
  const parts = [
    `${TPA_BRANDING_COOKIE}=${encoded}`,
    "Path=/",
    `Expires=${new Date(restore.expiresAt).toUTCString()}`,
    "SameSite=Lax",
  ];
  if (secure) parts.push("Secure");
  return parts.join("; ");
}

/**
 * One `c_balanceResponse[tpaId]` entry, the SOLE schema for it (server
 * readers in `smart-spender.server.ts`, the header tracker skeleton). Fields
 * validate independently, like `cTpaBlobSchema` above.
 *
 * `hasConsent` is tri-state: `true` on SUCCESS, `false` on CONSENT_REVOKED,
 * `null` otherwise (`balanceHelpers.js:207-215`). `memberId` stays `unknown`
 * because gate B1 distinguishes absent from present-but-empty.
 */
export const balanceEntrySchema = z.object({
  totalAvailableBalance: z.number().optional().catch(undefined),
  hasConsent: z.boolean().nullable().optional().catch(undefined),
  accounts: z.array(z.unknown()).optional().catch(undefined),
  memberId: z.unknown().optional().catch(undefined),
});

export type BalanceEntry = z.infer<typeof balanceEntrySchema>;
